import React, { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UploadCloud, Download, CheckCircle2, AlertCircle, SkipForward } from "lucide-react";
import { toast } from "sonner";

/** Bulk import of legacy patient master rows (CSV / XLSX).
 * Renders as a Settings sub-tab; server does all validation + de-duping. */
export default function DataImport() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/admin/import/patients/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "careo_patient_import_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not download template");
    }
  };

  const submit = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/import/patients", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported ${data.imported_count} of ${data.total} rows`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-ink">Import legacy patients</div>
            <div className="text-xs text-ink-muted">
              Upload old patient records from your previous system. Required columns:
              <b className="mx-1">UHID No</b>·<b className="mx-1">Patient Name</b>·<b className="mx-1">Date</b>.
              Optional: Mobile · Gender · Age · DOB · Address.
              Duplicate UHIDs are skipped.
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="import-download-template">
            <Download size={14} className="mr-1.5" />Download template
          </Button>
        </div>

        <div className="flex flex-col gap-3 rounded-md border border-dashed border-brand/40 bg-brand/5 p-4 sm:flex-row sm:items-center">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-xs"
            data-testid="import-file-input"
          />
          <Button onClick={submit} disabled={!file || uploading} className="bg-brand hover:bg-brand-700" data-testid="import-submit-btn">
            <UploadCloud size={14} className="mr-1.5" />
            {uploading ? "Uploading…" : "Upload & import"}
          </Button>
        </div>

        <div className="mt-2 text-[11px] text-ink-muted">
          Supported formats: <b>.csv</b> or <b>.xlsx</b>. Date accepted as DD-MM-YYYY, YYYY-MM-DD, or a real Excel date cell.
          Legacy UHID is stored as-is — new CareO patients continue to use the auto-generated UHID sequence.
        </div>
      </Card>

      {result && (
        <Card className="p-4" data-testid="import-result-card">
          <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat icon={UploadCloud} label="Total rows" value={result.total} tone="brand" />
            <Stat icon={CheckCircle2} label="Imported" value={result.imported_count} tone="emerald" testid="import-imported-count" />
            <Stat icon={SkipForward} label="Skipped (duplicates)" value={result.skipped_count} tone="amber" testid="import-skipped-count" />
            <Stat icon={AlertCircle} label="Errors" value={result.error_count} tone="rose" testid="import-error-count" />
          </div>

          {result.errors?.length > 0 && (
            <Section title={`Errors (${result.error_count})`}>
              <Table headers={["Row", "Reason"]} rows={result.errors.map((e) => [e.row, e.reason])} />
            </Section>
          )}

          {result.skipped?.length > 0 && (
            <Section title={`Skipped duplicates (${result.skipped_count})`}>
              <Table headers={["Row", "UHID", "Name", "Reason"]} rows={result.skipped.map((s) => [s.row, s.uhid, s.name, s.reason])} />
            </Section>
          )}

          {result.imported?.length > 0 && (
            <Section title={`Imported (${result.imported_count})`}>
              <Table headers={["Row", "UHID", "Name"]} rows={result.imported.map((i) => [i.row, i.uhid, i.name])} />
            </Section>
          )}
        </Card>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone, testid }) {
  const tones = {
    brand: "bg-brand/10 text-brand", emerald: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700", rose: "bg-rose-100 text-rose-700",
  };
  return (
    <div className="rounded-md border border-border bg-white p-2.5" data-testid={testid}>
      <div className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tones[tone] || tones.brand}`}>
        <Icon size={10} />{label}
      </div>
      <div className="mt-1 font-mono text-lg font-semibold text-ink">{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <details className="mt-2 rounded-md border border-border bg-white" open>
      <summary className="cursor-pointer px-2.5 py-1.5 text-xs font-semibold text-ink">{title}</summary>
      <div className="max-h-64 overflow-auto p-2">{children}</div>
    </details>
  );
}

function Table({ headers, rows }) {
  return (
    <table className="w-full text-[11px]">
      <thead>
        <tr className="border-b border-border text-left text-ink-muted">
          {headers.map((h, i) => <th key={i} className="px-1.5 py-1 font-medium">{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-border/60">
            {r.map((c, j) => <td key={j} className="px-1.5 py-1 font-mono">{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
